/* globals SENSOR_VERSION CLIENT_STORAGE_SCHEMA_VERSION */
import { shortNameMapping } from '../constants';
import { Utilities } from '../common';

export const createDefaultClrData = () => ({
  // TODO: create sane defaults?
  // site information
  siteInformation: {
    site: '', // site/angebotskennung
    code: '', // pagecode
    pixelType: '', // pixeltype
    event: '', // event
    refererHost: '', // referrer (host only)
    urlHost: '', // url (host only)
    surveyStatus: '', // survey-status
  },
  // device information
  deviceInformation: {
    userAgentString: '', // user-agent-string
    localTime: '', // localtime
    timeZone: '', // timezone, offset
    screenResolution: '', // screen-resolution
  },
  // technical information
  technicalInformation: {
    scriptVersion: '', // script-version
    runtime: '', // runtime
    scriptType: '', // sensor type (e.g. modern=es6+, legacy=es3)
    cryptoLibrary: '', // 1-char crypto library code (e.g. l=legacy, n=native)
    localData: '',
  },
  // client information
  clientInformation: {
    geoLocation: '', // geolocation
    errors: '', // errors
    language: '', // language
    privacySetting: '',
    // flags
    flags: {
      flagViewTime: 0, // flag-viewtime ({A, B})
      flagPiFrequency: 0, // flag-frequency (counter)
      flagPiReload: 0, // flag-reloads (counter)
      transmissionError: 0, // transmission-error (counter)
      flagPiBurst: 0, // flag-pi-burst (counter)
    },
  },
  // measured data
  measuredData: {
    // site
    site: {
      pi: 0, // pi
      visits: 0, // visit
      hourlyClients: 0, // client (hour)
      dailyClients: 0, // client (day)
      weeklyClients: 0, // client (week)
      monthlyClients: 0, // client (month)
      hourlyWnk: 0, // qualified hourly clients (wnk)
      sequenceNumber: 0, // A sequence number to mark page impressions which occurred in a visit session
      viewtime: 0,
    },
    // code
    code: {
      pi: 0, // pi
      visits: 0, // visit
      hourlyClients: 0, // client (hour)
      weeklyClients: 0, // client (week)
      dailyClients: 0, // client (day)
      monthlyClients: 0, // client (month)
    },
  },
  // profile
  profile: Utilities.createDefaultProfile(),
});

/**
 * Extracts the site information for the current client
 *
 * @param {ClrData} clrData - Current clr data model instance
 * @param {String} referrerHost - The referrer host of current document
 * @return {Object} Extracted client information
 */
const extractClientSiteInformation = (clrData, referrerHost) => {
  const {
    privacySettings,
    state,
  } = clrData.sensor;
  const params = state.globalParams;
  const information = {
    site: params.site,
    siteHash: params.siteHash,
    code: params.code,
    pixelType: state.pixelType,
  };
  if (params.comment) {
    information.comment = params.comment;
  }
  if (params.privacySetting === privacySettings.none) {
    // Take host part of the provided referrer
    information.refererHost = referrerHost;
    // Take the host part of the origin from current site or when AMP is used from AMP
    information.urlHost = state.globalParams.amp === true ? Utilities.getUrlHost(state.globalParams.ampUrl) : window.location.host;
  }
  return information;
};

/**
 * Extract client device information
 *
 * @param {ClrData} clrData - The current clr data model instance
 * @return {Object} Extracted client device information
 */
const extractClientDeviceInformation = (clrData) => {
  const {
    privacySettings,
    state,
  } = clrData.sensor;
  let information = {};
  if (state.globalParams.privacySetting === privacySettings.none) {
    information = {
      userAgentString: window.navigator.userAgent,
      localTime: clrData.now,
      timeZone: state.now.getTimezoneOffset(),
      screenResolution: Utilities.getScreen(),
    };
  }
  return information;
};
/**
 * Retrieves some client data (site and device) and maps them over to the provided current data of the clr data instance.
 *
 * @param {ClrData} clrData - Current clr data model instance
 * @param {String} referrerHost - The referrer host of current document
 */
const extractClientData = (clrData, referrerHost) => {
  const { data, sensor, now } = clrData;
  const { state, clsData, basicSettings } = sensor;
  // Set site information
  data.siteInformation = {
    ...data.siteInformation,
    ...extractClientSiteInformation(clrData, referrerHost),
  };
  // Set device information
  data.deviceInformation = {
    ...data.deviceInformation,
    ...extractClientDeviceInformation(clrData),
  };
  // Set technical information
  data.technicalInformation = {
    ...data.technicalInformation,
    scriptVersion: SENSOR_VERSION,
    clientSchemaVersion: CLIENT_STORAGE_SCHEMA_VERSION,
    scriptType: basicSettings.scriptType,
    cryptoLibrary: basicSettings.cryptoLibrary,
    runtime: state.runtimes,
    storage: clsData.data.storage || { type: 'none' },
    localData: state.globalParams.localData,
  };
  // Set client information
  data.clientInformation = {
    ...data.clientInformation,
    errors: state.scriptErrors,
    // Determine the client age when first event time is greater 0
    clientDays: clsData.data.site.firstEvent > 0 ? Utilities.diffDays(clsData.data.site.firstEvent, now) : 0,
    // Determine the client state (n = new client, r = recurring client)
    clientState: clsData.data.site.firstEvent === 0 ? 'n' : 'r',
    hybrid: {
      detected: state.hybrid.detected,
      platform: state.hybrid.platform,
    },
    privacySetting: state.globalParams.privacySetting,
    amp: state.globalParams.amp,
  };
};
/**
 * Clr data model class
 * @class
 */
export default class ClrData {
  /**
   * Initiates a new instance of the clr data model class
   *
   * @param {Sensor} sensor - The current sensor instance
   */
  constructor(sensor) {
    this.name = 'ClrDataClass';
    this.sensor = sensor;
    this.create();
  }

  /**
   * Calculates the metrics
   */
  calculateData(startTime = new Date().getTime(), timeCode = 'T3') {
    // Todo: Split this mess up into more readable parts
    const {
      basicSettings,
      clsData,
      logger,
      privacySettings,
      state,
      systemConfig,
    } = this.sensor;
    const lastSiteTransaction = clsData.data.site.lastTransaction;
    const referrer = state.globalParams.amp === true ? state.globalParams.ampReferrer : document.referrer;
    const referrerHost = Utilities.getUrlHost(referrer);
    const isPiEvent = state.globalParams.eventHandling === false
      || (state.globalParams.eventHandling === true && basicSettings.piEventList.indexOf(state.globalEvent) > -1);
    const lastTimeSite = new Date();
    lastTimeSite.setTime(lastSiteTransaction);
    const isValidReferrer = !systemConfig.respectReferer || (Utilities.checkValidLocation(this.sensor, Utilities.getLocation(referrer)) || referrer === '');
    const hasPrivacyDisabled = state.globalParams.privacySetting === privacySettings.none;
    const current = Utilities.dismantleDate(state.now);
    const last = Utilities.dismantleDate(lastTimeSite);
    const currentHour = current.hour;
    const lastHour = last.hour;
    const currentDay = current.day;
    const currentWeek = current.weekNumber;
    const lastDay = last.day;
    const lastWeek = last.weekNumber;
    const currentMonth = current.month;
    const lastMonth = last.month;
    logger.debug('Metric calculation started.');
    // Start metric calculation with extracting and combining client data with current data
    extractClientData(this, referrerHost);
    if (hasPrivacyDisabled === true) {
      this.data.clientInformation.geoLocation = 'unknown';
      this.data.clientInformation.language = navigator.userLanguage || navigator.language;
    }
    // Calculate site centric metrics
    if (isPiEvent) {
      this.data.measuredData.site.pi = 1;
      logger.debug('New page impression!');
    }
    if (state.persistence === true && isPiEvent === true) {
      // Calculate visit and sequence number when necessary
      if (lastSiteTransaction === 0 || (this.now - lastSiteTransaction > systemConfig.sessionTimeout && isValidReferrer)) {
        this.data.measuredData.site.visits = 1;
        logger.debug('New visit!');
        // When profiling is enabled store current time as session start in client flags
        if (systemConfig.profiling.enabled === true) {
          this.data.clientInformation.flags.sessionStart = this.now;
        }
      }
      // Create a sequence number to tag pis which occurred in a visit time frame. When a new visit starts the sequence number is always 1. If we are
      // within the visit time frame, the sequence number is counted up with the value from the last transaction.This only works when persistence is enabled,
      // it's a pi related event and the advanced privacy mode is deactivated in the current user agent.
      if (lastSiteTransaction === 0 || (isValidReferrer === true && hasPrivacyDisabled === true)) {
        this.data.measuredData.site.sequenceNumber = 1 + (this.data.measuredData.site.visits > 0 ? 0 : clsData.data.site.lastSequenceNumber);
        logger.debug('New sequence number:', this.data.measuredData.site.sequenceNumber, '!');
      }
      // Add new hourly client when requirement is met
      if (lastSiteTransaction === 0 || (isValidReferrer && currentHour !== lastHour)) {
        this.data.measuredData.site.hourlyClients = 1;
        logger.debug('New hourly client!');
      }
      // Add new hourly wnk when requirement is met
      if (systemConfig.profiling.enabled === true && clsData.data.profile.site.pi >= 1) {
        this.data.measuredData.site.hourlyWnk = 1;
        logger.debug('New wnk!');
      }
      // Add new daily client when requirement is met
      if (lastSiteTransaction === 0 || (isValidReferrer && currentDay !== lastDay)) {
        this.data.measuredData.site.dailyClients = 1;
        logger.debug('New daily client!');
      }
      // Add new weekly client when requirement is met
      if (lastSiteTransaction === 0 || (isValidReferrer && currentWeek !== lastWeek)) {
        logger.debug('New weekly client!');
        this.data.measuredData.site.weeklyClients = 1;
      }
      if (lastSiteTransaction === 0 || (isValidReferrer && currentMonth !== lastMonth)) {
        this.data.measuredData.site.monthlyClients = 1;
        logger.debug('New monthly client!');
      }
    }
    // Calculate viewtime
    if (state.persistence === true) {
      if (lastSiteTransaction !== 0 && this.now - lastSiteTransaction < systemConfig.viewtimeTimeout) {
        this.data.measuredData.site.viewtime = this.now - lastSiteTransaction;
        logger.debug(`Viewtime: ${this.data.measuredData.site.viewtime}`);
      } else {
        this.data.measuredData.site.viewtime = 0;
        logger.debug('No viewtime!');
      }
      // if last event time is fresher than last pi
      if (lastSiteTransaction !== '' && clsData.data.site.eventTime !== '' && lastSiteTransaction < clsData.data.site.eventTime
        && this.now - clsData.data.site.eventTime < systemConfig.viewtimeTimeout
      ) {
        logger.debug('Using last event time for viewtime.');
        this.data.measuredData.site.viewtime = this.now - clsData.data.site.eventTime;
        logger.debug(`New viewtime: ${this.data.measuredData.site.viewtime} !`);
      }
    } else {
      this.data.measuredData.site.viewtime = 0;
    }
    // Calculate site code metrics when it's a pi event
    if (isPiEvent === true) {
      this.data.measuredData.code.pi = 1;
    }
    if (state.persistence === true && isPiEvent === true) {
      const lastTimeCode = new Date();
      let lastCodeTransaction = lastTimeCode.getTime();
      const hasCode = Object.keys(clsData.data.code).indexOf(state.globalParams.code) > -1;
      if (hasCode === true) {
        lastTimeCode.setTime(clsData.data.code[state.globalParams.code].lastTransaction);
        lastCodeTransaction = clsData.data.code[state.globalParams.code].lastTransaction;
      }
      // Dismantle last time for code and extract hour, day and month properties
      const {
        hour: lastTimeCodeHourly,
        day: lastTimeCodeDaily,
        weekNumber: lastTimeCodeWeekly,
        month: lastTimeCodeMonthly,
      } = Utilities.dismantleDate(lastTimeCode);
      // Add visit when requirement is met
      if (hasCode === false || (this.now - lastCodeTransaction) > systemConfig.sessionTimeout) {
        this.data.measuredData.code.visits = 1;
      }
      // Add hourly client when requirement is met
      if (hasCode === false || currentHour !== lastTimeCodeHourly) {
        this.data.measuredData.code.hourlyClients = 1;
      }
      // Add daily client when requirement is met
      if (hasCode === false || currentDay !== lastTimeCodeDaily) {
        this.data.measuredData.code.dailyClients = 1;
      }
      // Add weekly client when requirement is met
      if (hasCode === false || currentWeek !== lastTimeCodeWeekly) {
        this.data.measuredData.code.weeklyClients = 1;
      }
      // Add monthly client when requirement is met
      if (hasCode === false || currentMonth !== lastTimeCodeMonthly) {
        this.data.measuredData.code.monthlyClients = 1;
      }
    }

    // Map over past profiling information from local storage when profiling should be used.
    if (systemConfig.profiling.enabled === true && state.persistence === true && hasPrivacyDisabled === true) {
      this.data.profile = { ...clsData.data.profile };
    }
    state.runtimes += `${timeCode}:${new Date().getTime() - startTime}`;
    logger.debug('Metric calculation finished.');
  }

  /**
   * Getter for the current data which keys will be abbreviated before returned
   *
   * @return {Object} Current data with abbreviated keys
   */
  getData() {
    const { logger } = this.sensor;
    logger.debug('CLRData before key abbreviation: ', this.data);
    const returnValue = Utilities.transformKeys(this.data, shortNameMapping);
    logger.debug('CLRData after key abbreviation: ', returnValue);
    return returnValue;
  }

  /**
   * Sets the sensor event in current data
   *
   * @param {String} sensorEvent
   */
  setEvent(sensorEvent) {
    this.data.siteInformation.event = sensorEvent;
  }

  /**
   * Creates a new data set for the relay client
   */
  create() {
    this.data = createDefaultClrData();
    this.now = this.sensor.state.now.getTime();
  }
}
